<script setup lang="ts">
import { computed, onMounted, ref, shallowRef, watch } from 'vue';
import i18n from '@/localisation/i18n';
import BrokerUserDialog from '@/modules/broker-admin/components/BrokerUserDialog.vue';
import { RestOperation } from '@/utils/helpers/rest';
import { errorString, i18nServerMessage } from '@/utils/helpers/rest-response';
import { AgTableClient } from '@/modules/common/components/ag-table';
import * as cols from '@/modules/common/components/ag-table/columns/users';
import { ColDef } from 'ag-grid-enterprise';
import { User, UserListResponse, UserRequest } from '@/modules/user-accounts/models';
import { Company, CompanyListResponse } from '@/modules/common/models/admin';
import axios, { AxiosError } from 'axios';
import { ApiError } from '@/utils/errors/api-error';
import { useDialogManager } from '@/plugins/dialog-manager';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import logger from '@/modules/common/services/logger.service';
import { useRouter } from '@/router/router';
import { useSocketStore } from '@/store/socket-store';
import { store as vuex } from '@/store/store';

const router = useRouter();
const dialog = useDialogManager();
const snackbar = useSnackbarManager();
const socketStore = useSocketStore();

socketStore.useEventHandler('refreshUsers', async () => {
  await fetchBrokerAdminUsers();
});

const defaultUser = UserRequest.create();

// Props
const props = defineProps<{
  userFilter?: string;
}>();

// Local state
const users = shallowRef<User[]>([]);
const companies = shallowRef<Company[]>([]);

const tableSearch = ref('');
const setQuickFilterText = shallowRef<(text: string) => void>(() => {
  /* noop */
});

const addBuffer = ref<UserRequest | null>(null);
const dialogBuffer = ref<UserRequest | null>(null);
const dialogOptions = ref({
  isActive: false,
  crudAction: RestOperation.Noop,
  title: 'User',
  cancelOptions: ['escape', 'outside'], // no 'X' for real FORMs
});

const hasBrokerComplianceRepRole = computed(() => vuex.getters.hasBrokerComplianceRepRole);

const columnDefs = computed<ColDef[]>(() => [
  cols.name(),
  cols.longCompanyName(),
  cols.email(),
  cols.role(),
  cols.tradingPermissions(),
  cols.brokerAccountStatus({
    approve: approve,
    hasBrokerComplianceRepRole: hasBrokerComplianceRepRole.value,
  }),
  cols.tfa(),
  cols.lastLogin(),
  cols.lastActive(), // @TODO: test it
  cols.totalActiveTime(), // @TODO: test it
  cols.actions({
    editUser: editUser,
    deleteUser: deleteUser,
    disableUser: disableUser,
    enableUser: enableUser,
  }),
]);

// Watchers
watch(
  () => props.userFilter,
  (newFilter) => {
    tableSearch.value = newFilter || '';
  }
);

watch(tableSearch, (newQuery) => {
  // update the query params
  void router.replace({ query: { filter: newQuery } });
});

function onReady(config: { setQuickFilterText: (text: string) => void }): void {
  setQuickFilterText.value = config.setQuickFilterText;
}

onMounted(async () => {
  tableSearch.value = props.userFilter || '';

  try {
    await fetchBrokerAdminUsers();
    await fetchBrokerCompanies();
  } catch (e) {
    logger.warn(e);
  }
});

async function fetchBrokerAdminUsers(): Promise<void> {
  try {
    const { data } = await axios.get('/api/1/broker-admin/users');
    const response = UserListResponse.fromData(data);
    users.value = response.users;
  } catch (e) {
    throw new ApiError(i18nServerMessage(e as AxiosError));
  }
}

async function fetchBrokerCompanies(): Promise<void> {
  const response = await axios.get('/api/1/broker-admin/companies');
  const parsed = CompanyListResponse.fromData(response.data);
  companies.value = parsed.companies;
}

async function deleteBrokerAdminUser(user: User): Promise<void> {
  try {
    await axios.delete(`/api/1/broker-admin/users/${user.id}`);
  } catch (e) {
    throw new ApiError(
      i18nServerMessage(e as AxiosError, `${user.emailAddress} cannot be deleted`)
    );
  }
}

async function disableBrokerAdminUser(user: User): Promise<void> {
  try {
    await axios.put(`/api/1/broker-admin/users/${user.id}/disable`);
  } catch (e) {
    throw new ApiError(
      i18nServerMessage(e as AxiosError, `${user.emailAddress} cannot be disabled`)
    );
  }
}

async function enableBrokerAdminUser(user: User): Promise<void> {
  try {
    await axios.put(`/api/1/broker-admin/users/${user.id}/enable`);
  } catch (e) {
    throw new ApiError(
      i18nServerMessage(e as AxiosError, `${user.emailAddress} cannot be enabled`)
    );
  }
}

async function approveBrokerAdminUser(user: User): Promise<void> {
  try {
    await axios.put(`/api/1/broker-admin/users/${user.id}/approve`);
  } catch (e) {
    throw new ApiError(
      i18nServerMessage(e as AxiosError, `${user.emailAddress} cannot be approved`)
    );
  }
}

async function approveBrokerAdminRoles(user: User): Promise<void> {
  try {
    await axios.put(`/api/1/broker-admin/users/${user.id}/approve-roles`);
  } catch (e) {
    throw new ApiError(
      i18nServerMessage(e as AxiosError, `${user.emailAddress} role change cannot be approved`)
    );
  }
}

function getRowId(userAccount: User): string {
  return userAccount.id;
}

function createUser(): void {
  dialogOptions.value.isActive = true;
  dialogOptions.value.crudAction = RestOperation.Create;
  dialogOptions.value.title = 'Add User';

  // addBuffer is a 2nd buffer behind the dialog so that the admin
  // can escape from `Create New User` and return with partially filled record
  // browsing the user list will not destroy the partially filled record
  if (addBuffer.value == null) {
    addBuffer.value = UserRequest.clone(defaultUser);
  }
  dialogBuffer.value = addBuffer.value;
}

function editUser(rowUser: User): void {
  dialogOptions.value.isActive = true;
  dialogOptions.value.crudAction = RestOperation.Update;
  dialogOptions.value.title = 'Update User Profile';
  // do not modify the row while typing
  dialogBuffer.value = UserRequest.fromUser(rowUser);
}

function closeModal(): void {
  // wipe user data
  dialogOptions.value.isActive = false;
  dialogOptions.value.crudAction = RestOperation.Noop;
  dialogOptions.value.title = '';
  addBuffer.value = null;
  dialogBuffer.value = null;
}

function deleteUser(rowUser: User): void {
  dialog.ask({
    title: i18n.t('adminDeleteUser'),
    color: 'error',
    icon: 'mdi-alert',
    message: i18n.t('adminDeleteUserMessage', {
      userName: rowUser.name,
      companyName: rowUser.companyName,
      emailAddress: rowUser.emailAddress,
    }),
    acceptText: i18n.t('adminDeleteUser'),
    onAccept: () => doDeleteUser(rowUser),
    rejectText: i18n.t('cancelButton'),
    shouldManuallyConfirm: true,
  });
}

function disableUser(rowUser: User): void {
  dialog.ask({
    title: i18n.t('adminDisableUser'),
    color: 'warning',
    icon: 'mdi-account-off',
    message: i18n.t('adminDisableUserMessage', {
      userName: rowUser.name,
      companyName: rowUser.companyName,
      emailAddress: rowUser.emailAddress,
    }),
    acceptText: i18n.t('adminDisableUser'),
    onAccept: () => doDisableUser(rowUser),
    rejectText: i18n.t('cancelButton'),
    shouldManuallyConfirm: true,
  });
}

function enableUser(rowUser: User): void {
  dialog.ask({
    title: i18n.t('adminEnableUser'),
    color: 'info',
    icon: 'mdi-account-check',
    message: i18n.t('adminEnableUserMessage', {
      userName: rowUser.name,
      companyName: rowUser.companyName,
      emailAddress: rowUser.emailAddress,
    }),
    acceptText: i18n.t('adminEnableUser'),
    onAccept: () => doEnableUser(rowUser),
    rejectText: i18n.t('cancelButton'),
  });
}

function approve(rowUser: User): void {
  if (rowUser.accountStatus === 'pending-approval') {
    approveUser(rowUser);
  }
  if (rowUser.accountStatus === 'pending-approval-for-roles') {
    approveUserRoles(rowUser);
  }
}

function approveUser(rowUser: User): void {
  dialog.ask({
    title: i18n.t('adminApproveUser'),
    color: 'info',
    icon: 'mdi-account-check',
    message: i18n.t('adminApproveUserMessage', {
      userName: rowUser.name,
      companyName: rowUser.companyName,
      emailAddress: rowUser.emailAddress,
      role: i18n.t(rowUser.roles[0]),
      tradingPermissions:
        rowUser.tradingPermissions !== null
          ? i18n.t(`tradingPermissions.options.${rowUser.tradingPermissions}`)
          : i18n.t(`tradingPermissions.inherit`),
    }),
    acceptText: i18n.t('adminApproveUser'),
    onAccept: () => doApproveUser(rowUser),
    rejectText: i18n.t('cancelButton'),
  });
}

function approveUserRoles(rowUser: User): void {
  dialog.ask({
    title: i18n.t('adminApproveRoles'),
    color: 'info',
    icon: 'mdi-account-check',
    message: i18n.t('adminApproveRolesMessage', {
      userName: rowUser.name,
      companyName: rowUser.companyName,
      emailAddress: rowUser.emailAddress,
      role: i18n.t(rowUser.roles[0]),
      newRole: i18n.t(`${rowUser.rolesPendingApproval && rowUser.rolesPendingApproval[0]}`),
      tradingPermissions:
        rowUser.tradingPermissions !== null
          ? i18n.t(`tradingPermissions.options.${rowUser.tradingPermissions}`)
          : i18n.t(`tradingPermissions.inherit`),
    }) as string,
    acceptText: i18n.t('adminApproveRoles') as string,
    onAccept: () => doApproveRoles(rowUser),
    rejectText: i18n.t('cancelButton'),
  });
}

async function doDeleteUser(rowUser: User): Promise<void> {
  try {
    await deleteBrokerAdminUser(rowUser);

    snackbar.confirm(i18n.tc('adminDeletedUserMessage'));
  } catch (e) {
    snackbar.error(errorString(e as Error));
  }
}

async function doDisableUser(rowUser: User): Promise<void> {
  try {
    await disableBrokerAdminUser(rowUser);

    snackbar.confirm(i18n.tc('adminDisabledUser'));
  } catch (e) {
    snackbar.error(errorString(e as Error));
  }
}

async function doEnableUser(rowUser: User): Promise<void> {
  try {
    await enableBrokerAdminUser(rowUser);

    snackbar.confirm(i18n.tc('adminEnabledUser'));
  } catch (e) {
    snackbar.error(errorString(e as Error));
  }
}

async function doApproveUser(rowUser: User): Promise<void> {
  try {
    await approveBrokerAdminUser(rowUser);

    snackbar.confirm(i18n.tc('adminApprovedUser'));
  } catch (e) {
    snackbar.error(errorString(e as Error));
  }
}

async function doApproveRoles(rowUser: User): Promise<void> {
  try {
    await approveBrokerAdminRoles(rowUser);

    snackbar.confirm(i18n.tc('adminApprovedRoles'));
  } catch (e) {
    snackbar.error(errorString(e as Error));
  }
}
</script>

<!-- eslint-disable vue/no-v-html -->
<template>
  <VCard class="flex d-flex flex-column fill-height">
    <VCardText>
      <!-- add user account -->
      <VBtn class="ma-6" color="primary" data-test-id="add-user-button" @click="createUser">
        <VIcon left> mdi-plus</VIcon>
        {{ $t('adminAddUser') }}
      </VBtn>

      <!-- search bar -->
      <VTextField
        v-model="tableSearch"
        class="mx-6"
        clearable
        :label="$t('adminBrokerSearchUser')"
        prepend-inner-icon="mdi-magnify"
        @input="setQuickFilterText(tableSearch)"
      ></VTextField>
    </VCardText>

    <AgTableClient
      v-if="users"
      :column-defs="columnDefs"
      :get-row-id="getRowId"
      :page-size="1000"
      :row-data="users"
      :sort="{ colId: 'name', sort: 'asc' }"
      table-id="BrokerUsers"
      @ready="onReady"
    />

    <!-- at least 1 company before adding users! -->
    <VAlert v-if="!companies.length" border="left" colored-border elevation="2" text type="warning">
      <span v-html="$t('noTraderCompanies')"></span>
    </VAlert>

    <!-- edit user popup -->
    <BrokerUserDialog
      v-if="dialogOptions.isActive && dialogBuffer"
      :crud-action="dialogOptions.crudAction"
      :title="dialogOptions.title"
      :user="dialogBuffer"
      @close-modal="closeModal"
    ></BrokerUserDialog>
  </VCard>
</template>

<style lang="scss" scoped></style>
